/**
 * Tooltip met lichte animatie + optioneel geluid (geen geluid bij reduced-motion).
 */
import { useId, useState, useRef, useCallback, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { playUiTick } from '../../lib/uiSounds';

type Side = 'top' | 'bottom';

export function Tooltip({
  content,
  children,
  side = 'top',
  className = '',
  label,
  sound = true,
  iconOnly,
}: {
  content: ReactNode;
  children?: ReactNode;
  side?: Side;
  className?: string;
  /** Toegankelijke korte naam */
  label?: string;
  sound?: boolean;
  /** Alleen ?-icoon */
  iconOnly?: boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    setOpen(true);
    if (sound) playUiTick();
  }, [sound]);

  const hide = useCallback(() => {
    leaveTimer.current = setTimeout(() => setOpen(false), 120);
  }, []);

  const instantHide = useCallback(() => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    setOpen(false);
  }, []);

  const ariaLabel = label ?? 'Meer uitleg';

  return (
    <span
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={instantHide}
    >
      {iconOnly ? (
        <button
          type="button"
          className="inline-flex rounded-full p-0.5 text-text-muted transition hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          aria-describedby={open ? id : undefined}
          aria-label={ariaLabel}
        >
          <HelpCircle className="h-4 w-4" strokeWidth={2} aria-hidden />
        </button>
      ) : (
        <span className="inline-flex items-center gap-1" aria-describedby={open ? id : undefined}>
          {children}
          <button
            type="button"
            tabIndex={-1}
            className="inline-flex shrink-0 rounded-full p-0.5 text-text-muted hover:bg-primary/10 hover:text-primary"
            aria-hidden
          >
            <HelpCircle className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </span>
      )}
      {open && (
        <span
          id={id}
          role="tooltip"
          className={`tooltip-bubble pointer-events-none absolute z-[60] w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-border bg-surface-card px-3 py-2 text-xs leading-relaxed text-text shadow-lg ${
            side === 'top' ? 'bottom-full left-1/2 mb-2 -translate-x-1/2' : 'top-full left-1/2 mt-2 -translate-x-1/2'
          }`}
        >
          {content}
        </span>
      )}
    </span>
  );
}
